'use client'
import {
  __async,
  __objRest,
  __spreadProps,
  __spreadValues
} from "./chunk-UYSR6ZT4.mjs";

// src/components/events/buttons/NotifyButton.tsx
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useGeolocation, useRouter, useTranslation } from "@wbk/config";
import { twMerge } from "tailwind-merge";
import { Button, Input, MobileInput, ResponsiveModal, Select, Typography } from "@wbk/ui";
import { EMAIL_PATTERN } from "@wbk/utils";
import { useGetTickets, useRegisterInterest } from "@wbk/api/hooks";
import { AnimatedCheck, BellIcon } from "@wbk/svg";
import { WithGrecaptcha } from "@wbk/api/grecaptcha";
import { useAnalytics } from "@wbk/analytics";
import { useAuth } from "@wbk/auth";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";
var NotifyButton = (_a) => {
  var _b = _a, {
    event,
    className,
    type = "event",
    textVariant = "label-L"
  } = _b, props = __objRest(_b, [
    "event",
    "className",
    "type",
    "textVariant"
  ]);
  return /* @__PURE__ */ jsx(
    ResponsiveModal,
    {
      action: false,
      cancel: false,
      render: ({ open }) => /* @__PURE__ */ jsx(
        ActionButton,
        __spreadValues({
          event,
          onClick: open,
          className,
          textVariant
        }, props)
      ),
      body: ({ close }) => /* @__PURE__ */ jsx(Form, { event, type, onClose: close })
    }
  );
};
var Form = ({
  event,
  onClose,
  type = "event"
}) => {
  var _a, _b, _c;
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation("contentful_common");
  const { locale } = useRouter();
  const { country: defaultCountry } = useGeolocation();
  const isHyExperience = !!event.hyProperties;
  const { data } = useGetTickets(
    {
      lang: locale,
      slug: event.slug,
      ticketType: type
    },
    { enabled: !isHyExperience }
  );
  const showTicketForm = ((_a = data == null ? void 0 : data.event) == null ? void 0 : _a.notify_me_type) === "ticket" && !!((_b = data == null ? void 0 : data.tickets) == null ? void 0 : _b.length);
  const {
    control,
    register,
    setValue,
    handleSubmit,
    formState: { errors },
    watch
  } = useForm({
    defaultValues: {
      name: "",
      phone: "",
      email: ""
    }
  });
  const { registerInterestEvent } = useAnalytics();
  const { mutateAsync: registerInterest, isPending, error } = useRegisterInterest();
  const selectedCountry = watch("country") || defaultCountry;
  const { user } = useAuth();
  useEffect(() => {
    if (defaultCountry) {
      setValue("country", defaultCountry);
    }
  }, [defaultCountry]);
  const onSubmit = (data2) => __async(null, null, function* () {
    const values = {
      name: (user == null ? void 0 : user.name) || data2.name,
      email: (user == null ? void 0 : user.email) || data2.email,
      country_code: (user == null ? void 0 : user.cell_country_code) || data2.country.dial_code,
      phone: (user == null ? void 0 : user.cell_number) || data2.phone,
      venue_id: event.id,
      slug: event.slug,
      ticket_category: data2.ticket_category,
      ticket_amount: data2.ticket_amount,
      type: event.__typename.toLowerCase(),
      lang: locale
    };
    try {
      yield registerInterest(values);
      registerInterestEvent(values);
      setSuccess(true);
    } catch (err) {
    }
  });
  return /* @__PURE__ */ jsx(WithGrecaptcha, { children: /* @__PURE__ */ jsx("form", { onSubmit: handleSubmit(onSubmit), className: "space-y-4", children: success ? /* @__PURE__ */ jsxs("div", { className: "space-y-6", children: [
    /* @__PURE__ */ jsx(AnimatedCheck, { className: "text-success mx-auto h-40 w-40" }),
    /* @__PURE__ */ jsx(Typography, { as: "h2", variant: "heading-M", className: "text-center", children: t("contentful_common:notify.notify_me_success", { title: event.title }) }),
    /* @__PURE__ */ jsx(
      Button,
      {
        type: "button",
        shape: "outlined",
        theme: "action",
        className: "w-full",
        onClick: onClose,
        children: /* @__PURE__ */ jsx(Typography, { variant: "label-M", children: t("contentful_common:back") })
      }
    )
  ] }) : /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsxs("div", { className: "space-y-1", children: [
      /* @__PURE__ */ jsx(Typography, { as: "h2", variant: "heading-M", children: t("contentful_common:notify.notify_me_title", { title: event.title }) }),
      /* @__PURE__ */ jsx(Typography, { variant: "body-S", color: "text-secondary", children: t("contentful_common:notify.notify_me_desc") })
    ] }),
    !user && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Input,
        __spreadProps(__spreadValues({
          label: t("contentful_common:notify.name"),
          id: "name",
          error: ((_c = errors.name) == null ? void 0 : _c.message) || (error == null ? void 0 : error.message)
        }, register("name", {
          required: t("contentful_common:validation.required")
        })), {
          onChange: (value) => {
            setValue("name", value);
          }
        })
      ),
      /* @__PURE__ */ jsx(
        Controller,
        {
          control,
          name: "email",
          rules: {
            required: t("contentful_common:validation.required"),
            pattern: {
              value: EMAIL_PATTERN,
              message: t("contentful_common:validation.invalid_email")
            }
          },
          defaultValue: "",
          render: (_d) => {
            var { field: _e } = _d, _f = _e, { onChange } = _f, props = __objRest(_f, ["onChange"]);
            var _a2;
            return /* @__PURE__ */ jsx(
              Input,
              __spreadProps(__spreadValues({}, props), {
                type: "email",
                label: t("contentful_common:notify.email"),
                placeholder: "you@email.com",
                onChange: (value) => {
                  onChange(value);
                },
                error: (_a2 = errors.email) == null ? void 0 : _a2.message
              })
            );
          }
        }
      ),
      /* @__PURE__ */ jsx(
        Controller,
        {
          name: "phone",
          control,
          rules: {
            required: t("contentful_common:validation.required"),
            validate: (value) => {
              var _a2, _b2;
              const hasValidation = (_a2 = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _a2.length;
              if (hasValidation) {
                const validStart = (_b2 = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _b2.some(
                  (start) => value.startsWith(start)
                );
                return validStart || t("contentful_common:validation.invalid_mobile");
              }
              return true;
            }
          },
          render: (_g) => {
            var { field: _h } = _g, _i = _h, { value } = _i, props = __objRest(_i, ["value"]);
            var _a2, _b2, _c2, _d;
            return /* @__PURE__ */ jsx(
              MobileInput,
              __spreadProps(__spreadValues({}, props), {
                label: t("contentful_common:notify.mobile"),
                country: selectedCountry,
                mobile: value,
                portal: false,
                onChange: (name, value2) => {
                  if (name === "country") {
                    setValue("country", value2);
                  } else {
                    setValue("phone", value2);
                  }
                },
                error: ((_a2 = errors == null ? void 0 : errors.phone) == null ? void 0 : _a2.message) || ((_b2 = errors == null ? void 0 : errors.country) == null ? void 0 : _b2.message),
                placeholder: selectedCountry ? `${((_c2 = selectedCountry == null ? void 0 : selectedCountry.mobile_starts_with) == null ? void 0 : _c2[0]) || ""}${"x".repeat(
                  (((_d = selectedCountry.phone_number_lengths) == null ? void 0 : _d[0]) || 7) - 1
                )}` : ""
              })
            );
          }
        }
      )
    ] }),
    showTicketForm && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsx(
        Controller,
        {
          name: "ticket_category",
          control,
          rules: {
            required: t("contentful_common:validation.required")
          },
          defaultValue: "",
          render: (_j) => {
            var { field: _k } = _j, _l = _k, { onChange } = _l, props = __objRest(_l, ["onChange"]);
            var _a2, _b2;
            return /* @__PURE__ */ jsx(
              Select,
              __spreadProps(__spreadValues({}, props), {
                id: "ticket_category",
                autoComplete: "off",
                portal: false,
                onChange: (selected) => onChange((selected == null ? void 0 : selected.value) || ""),
                label: t("contentful_common:notify.ticket_category"),
                options: (_a2 = (data == null ? void 0 : data.tickets) || []) == null ? void 0 : _a2.map((ticket) => ({
                  id: ticket._id,
                  value: ticket._id,
                  text: ticket.title
                })),
                error: (_b2 = errors.ticket_category) == null ? void 0 : _b2.message
              })
            );
          }
        },
        "ticket_category"
      ),
      /* @__PURE__ */ jsx(
        Controller,
        {
          name: "ticket_amount",
          control,
          rules: {
            required: t("contentful_common:validation.required")
          },
          defaultValue: "",
          render: (_m) => {
            var { field: _n } = _m, _o = _n, { onChange } = _o, props = __objRest(_o, ["onChange"]);
            var _a2;
            return /* @__PURE__ */ jsx(
              Select,
              __spreadProps(__spreadValues({}, props), {
                id: "ticket_amount",
                autoComplete: "off",
                portal: false,
                onChange: (selected) => onChange((selected == null ? void 0 : selected.value) || ""),
                label: t("contentful_common:notify.ticket_amount"),
                options: Array.from({ length: 3 }, (_, i) => i + 1).map((amount) => ({
                  id: amount.toString(),
                  value: amount.toString(),
                  text: amount.toString()
                })),
                error: (_a2 = errors.ticket_category) == null ? void 0 : _a2.message
              })
            );
          }
        },
        "ticket_amount"
      )
    ] }),
    /* @__PURE__ */ jsx("div", { className: "pt-4", children: /* @__PURE__ */ jsx(Button, { className: "w-full", type: "submit", loading: isPending, children: /* @__PURE__ */ jsx(Typography, { variant: "label-L", children: t("contentful_common:notify.notify_me") }) }) })
  ] }) }) });
};
var ActionButton = (_a) => {
  var _b = _a, { event, className, onClick, textVariant = "label-L" } = _b, props = __objRest(_b, ["event", "className", "onClick", "textVariant"]);
  const { t } = useTranslation("contentful_common");
  const { trackEvent } = useAnalytics();
  const handleClick = (e) => {
    e.stopPropagation();
    e.preventDefault();
    trackEvent({
      event: "notify_me_clicked",
      data: {
        slug: event.slug
      }
    });
    onClick == null ? void 0 : onClick(e);
  };
  return /* @__PURE__ */ jsxs(
    Button,
    __spreadProps(__spreadValues({
      onClick: handleClick,
      className: twMerge("min-h-[50px] w-full gap-2 leading-none", className)
    }, props), {
      children: [
        /* @__PURE__ */ jsx(BellIcon, { className: "h-4 w-4" }),
        /* @__PURE__ */ jsx(Typography, { variant: textVariant, children: (event == null ? void 0 : event.buttonLabel) || t("contentful_common:notify.notify_me") })
      ]
    })
  );
};
var NotifyButton_default = NotifyButton;

export {
  NotifyButton_default
};
//# sourceMappingURL=chunk-WZIEBXDX.mjs.map